import React, { useState } from 'react';
import { Box, Typography, TextField, Alert, CircularProgress } from '@mui/material';
import PrimaryButton from '../PrimaryButton';
import axiosClient from '../../services/axiosClient';

interface EmailCollectionScreenProps {
  onContinue: (email: string, existingProspect?: any) => void;
  onBack: () => void;
  isMobile: boolean;
  listingId: string;
}

const EmailCollectionScreen: React.FC<EmailCollectionScreenProps> = ({
  onContinue,
  onBack,
  isMobile,
  listingId,
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleContinue = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setIsChecking(true);

    try {
      // Check if this email is associated with an existing prospect for this listing
      const response = await axiosClient.get(`/schedule-tour/${listingId}/check-prospect?email=${encodeURIComponent(email.trim())}`);
      const existingProspect = response.data; // This will be null if no prospect exists
      
      setIsChecking(false);
      onContinue(email.trim(), existingProspect);
    } catch (error) {
      console.error('Error checking existing prospect:', error);
      setIsChecking(false);
      // Continue anyway, just log the error
      onContinue(email.trim(), null);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (error) setError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isMobile ? 'left' : 'center',
        justifyContent: 'flex-start',
        marginTop: 0,
        padding: isMobile ? '1rem' : '2rem',
      }}
    >
      <Typography variant="h6" sx={{ mt: 6, mb: 4, textAlign: 'left', maxWidth: 500, fontWeight: 400 }}>
        Great! We just need your email address to send you tour confirmation details
      </Typography>
      
      <Box sx={{ width: '100%', maxWidth: 500, mb: 4 }}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          error={!!error}
          helperText={error}
          placeholder="Enter your email address"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: error ? '#d32f2f' : '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: error ? '#d32f2f' : '#bdbdbd',
              },
              '&.Mui-focused fieldset': {
                borderColor: error ? '#d32f2f' : '#1976d2',
              },
            },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, width: '100%', maxWidth: 500 }}>
        <PrimaryButton
          onClick={onBack}
          sx={{
            padding: '12px 24px',
            fontSize: '1rem',
            fontWeight: 500,
            backgroundColor: '#f5f5f5',
            color: '#333',
            '&:hover': {
              backgroundColor: '#e0e0e0',
            },
          }}
        >
          Back
        </PrimaryButton>
        
        <PrimaryButton
          onClick={handleContinue}
          disabled={isChecking}
          sx={{
            padding: '12px 30px',
            fontSize: '1.15rem',
            fontWeight: 500,
            flex: 1,
          }}
        >
          {isChecking ? <CircularProgress size={20} color="inherit" /> : 'Continue'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

export default EmailCollectionScreen; 